import {
  getGameDiscoveryPresentation,
  SuitGlyph,
  type GameDiscoveryPresentation,
} from '@cardforest/game-components/discovery';
import { hasGameRules } from '@cardforest/game-docs/rules';
import { type LobbyVisibility } from '@everyturn/protocol';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@workspace/app-components';
import { BookOpen, ArrowUp, Clock, ExternalLink, Layers, Play, Plus, Search, Sparkles, Users, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { GameInfoDialog } from '@/components/AppShell';
import type { GameInfo } from '@/hooks/use-play-mode';
import {
  cancelLobbyTable,
  createLobbyTable,
  getLobbyMembership,
  joinLobbyTable,
  leaveLobbyTable,
  listCommunityRequests,
  listLobbyTables,
  unvoteCommunityRequest,
  voteCommunityRequest,
  type ApiCommunityRequestItem,
  type ApiLobbyTable,
} from '@/lib/api';
import { games } from '@/lib/games';
import { useAuthStore } from '@/stores/auth-store';

/* ── Types & Game Catalog ─────────────────────────────── */

interface PlayableGame {
  id: string;
  name: string;
  subtitle: string;
  family: string;
  players: string;
  formatLabel: string;
  presentation?: GameDiscoveryPresentation;
  maxSeats: number;
}

const PLAYABLE_GAMES: readonly PlayableGame[] = games.list().map((game) => {
  if (!game.discovery) throw new Error(`Missing discovery metadata for '${game.gameId}'`);
  return {
    id: game.gameId,
    name: game.displayName,
    subtitle: game.discovery.summary,
    family: game.discovery.family,
    players: game.discovery.playerLabel,
    formatLabel: game.discovery.formatLabel,
    presentation: getGameDiscoveryPresentation(game.gameId),
    maxSeats: game.maxPlayers,
  };
});

/* ── Dedicated Multiplayer Tables Dialog ──────────────── */

function TableRow({
  table,
  onJoin,
  disabled,
}: {
  table: ApiLobbyTable;
  onJoin: (tableId: string) => void;
  disabled: boolean;
}) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isSeated = table.seats.some((s) => s.userId === user?.id);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2.5 px-3">
      <div className="flex items-center gap-2 text-sm min-w-0">
        <Users className="size-4 text-muted-foreground shrink-0" />
        <span className="font-medium">
          {table.filledSeats}/{table.maxSeats} seated
        </span>
        {table.status === 'started' && <Badge variant="secondary">In progress</Badge>}
        {table.status === 'starting' && <Badge variant="secondary">Loading</Badge>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {(isSeated || table.status !== 'waiting') && (
          <Button
            variant="outline"
            size="sm"
            className="min-h-11"
            disabled={disabled}
            onClick={() => navigate(`/tables/${encodeURIComponent(table.tableId)}`)}
          >
            View Table
          </Button>
        )}
        {!isSeated && table.status === 'waiting' && (
          <Button size="sm" className="min-h-11" disabled={disabled} onClick={() => onJoin(table.tableId)}>
            Join Table
          </Button>
        )}
      </div>
    </div>
  );
}

function MultiplayerDialog({
  game,
  tables,
  open,
  onOpenChange,
  onRefresh,
  myTable,
}: {
  game: PlayableGame;
  tables: ApiLobbyTable[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => void;
  myTable?: ApiLobbyTable;
}) {
  type TableAction = { type: 'host'; visibility: LobbyVisibility } | { type: 'join'; tableId: string };
  const [confirmAction, setConfirmAction] = useState<TableAction | null>(null);
  const [pending, setPending] = useState(false);
  const active = useRef(false);
  const actionPending = useRef(false);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const sessionToken = useAuthStore((s) => s.session?.token);
  const authRevision = useAuthStore((s) => s.revision);
  const authLoading = useAuthStore((s) => s.loading);
  const authError = useAuthStore((s) => s.error);
  const authReady = !authLoading && !!sessionToken && !!user;
  const blocked = pending || !authReady;
  const filtered = tables.filter((t) => t.gameType === game.id);

  useEffect(() => {
    active.current = true;
    actionPending.current = false;
    setPending(false);
    setConfirmAction(null);
    return () => {
      active.current = false;
    };
  }, [authRevision]);
  const isCurrent = () => active.current && useAuthStore.getState().revision === authRevision;

  const isAtThisGame = myTable?.gameType === game.id;
  const myTableGameName = myTable
    ? (PLAYABLE_GAMES.find((g) => g.id === myTable.gameType)?.name ?? myTable.gameType)
    : '';
  const isHost = myTable?.hostUserId === user?.id;

  const leaveCurrentTable = async (token: string) => {
    if (!myTable) return;
    if (myTable.hostUserId === user?.id) {
      await cancelLobbyTable(token, myTable.tableId);
    } else {
      await leaveLobbyTable(token, myTable.tableId);
    }
  };

  const perform = async (action: TableAction, leaveCurrent = false) => {
    if (!isCurrent() || actionPending.current) return;
    if (authLoading || !sessionToken || !user) {
      toast.error('Connect to multiplayer before hosting or joining a table.');
      return;
    }
    actionPending.current = true;
    setPending(true);
    let leftCurrent = false;
    try {
      if (leaveCurrent) {
        await leaveCurrentTable(sessionToken);
        if (!isCurrent()) return;
        leftCurrent = true;
        setConfirmAction(null);
        onRefresh();
      }
      const table =
        action.type === 'host'
          ? await createLobbyTable(sessionToken, game.id, game.maxSeats, action.visibility)
          : await joinLobbyTable(sessionToken, action.tableId);
      if (!isCurrent()) return;
      setConfirmAction(null);
      onRefresh();
      onOpenChange(false);
      navigate(`/tables/${encodeURIComponent(table.tableId)}`);
    } catch (error) {
      if (!isCurrent()) return;
      const message = error instanceof Error ? error.message : 'Could not update the table.';
      toast.error(leftCurrent ? `You left the previous table, but the next action failed: ${message}` : message);
    } finally {
      if (isCurrent()) {
        actionPending.current = false;
        setPending(false);
      }
    }
  };
  const requestAction = (action: TableAction) => {
    if (myTable) setConfirmAction(action);
    else void perform(action);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {game.presentation ? <game.presentation.Icon className="size-4" /> : <Layers className="size-4" />}
            <span>{game.name} — Multiplayer Tables</span>
          </DialogTitle>
          <DialogDescription>Join an open table or host a new room for {game.players.toLowerCase()}.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 pt-1">
          {authLoading ? (
            <p role="status">Connecting to multiplayer...</p>
          ) : !authReady ? (
            <Alert variant="destructive">
              <AlertTitle>Multiplayer unavailable</AlertTitle>
              <AlertDescription>
                <p>{authError ?? 'A server session is required to host or join a table.'}</p>
                <p>You can still close this dialog and play solo.</p>
                <Button variant="outline" className="min-h-11" onClick={() => void useAuthStore.getState().bootstrap()}>
                  Retry connection
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}
          {confirmAction && (
            <Alert variant="destructive">
              <AlertTitle>Switch tables?</AlertTitle>
              <AlertDescription>
                <p className="text-sm">
                  You&apos;re already {isHost ? 'hosting' : 'waiting at'} a <strong>{myTableGameName}</strong> table.
                  {isHost
                    ? ' Continuing will cancel your current table for everyone.'
                    : ' Continuing will leave your current seat.'}
                  {confirmAction.type === 'host'
                    ? ' Then you will host a new table.'
                    : ' Then you will join the selected table.'}
                </p>
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-11"
                    disabled={pending}
                    onClick={() => setConfirmAction(null)}
                  >
                    Keep Current
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="min-h-11"
                    disabled={blocked}
                    onClick={() => void perform(confirmAction, true)}
                  >
                    {isHost ? 'Abandon & Continue' : 'Leave & Continue'}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Open Tables ({filtered.length})
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="min-h-11"
                onClick={() => requestAction({ type: 'host', visibility: 'public' })}
                disabled={blocked || !!(myTable && isHost && isAtThisGame)}
              >
                <Plus data-icon="inline-start" />
                Host public table
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11"
                onClick={() => requestAction({ type: 'host', visibility: 'unlisted' })}
                disabled={blocked || !!(myTable && isHost && isAtThisGame)}
              >
                <Plus data-icon="inline-start" />
                Host unlisted table
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Unlisted tables are not shown in discovery. Anyone with the invitation can request a seat.
            </p>
          </div>

          {filtered.length === 0 ? (
            <Empty className="border py-6">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Users />
                </EmptyMedia>
                <EmptyTitle>No open {game.name} tables right now</EmptyTitle>
                <EmptyDescription>
                  Host a table above to wait for players, or close this dialog and click Play Solo to start immediately.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="divide-y border rounded-lg">
              {filtered.map((table) => (
                <TableRow
                  key={table.tableId}
                  table={table}
                  onJoin={(id) => requestAction({ type: 'join', tableId: id })}
                  disabled={blocked}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Active Lobby Table Banner ────────────────────────── */

function WaitingBanner({
  table,
  game,
  onView,
  onLeave,
}: {
  table: ApiLobbyTable;
  game: PlayableGame;
  onView: () => void;
  onLeave: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const isHost = table.hostUserId === user?.id;
  const filledSeats = table.seats.filter((s) => s.userId).length;

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-xl border border-primary/35 bg-primary/5 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Clock className="size-4 text-primary shrink-0 animate-pulse" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            {table.status === 'full' ? 'Waiting for host' : 'Waiting for players'} — {game.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {filledSeats}/{table.maxSeats} players seated · {isHost ? 'You are the host' : 'Waiting for host to start'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" className="min-h-9" onClick={onView}>
          View Table
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          className="min-h-9 min-w-9 text-muted-foreground hover:text-destructive"
          onClick={onLeave}
          aria-label={isHost ? 'Cancel table' : 'Leave table'}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

/* ── Featured Playable Game Card ──────────────────────── */

function PlayableGameCard({
  game,
  tableCount,
  onPlaySolo,
  onOpenOnline,
  onOpenRules,
}: {
  game: PlayableGame;
  tableCount: number;
  onPlaySolo: () => void;
  onOpenOnline: () => void;
  onOpenRules: (trigger: HTMLElement) => void;
}) {
  return (
    <Card className="justify-between transition-shadow hover:shadow-md">
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3.5 min-w-0">
            {game.presentation ? (
              <game.presentation.Thumbnail />
            ) : (
              <Layers aria-hidden="true" className="h-14 w-16 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-lg font-semibold tracking-tight">{game.name}</CardTitle>
                {tableCount > 0 && (
                  <Badge variant="default">
                    <Users data-icon="inline-start" />
                    {tableCount} {tableCount === 1 ? 'table' : 'tables'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 shrink-0 cursor-pointer"
            onClick={(e) => onOpenRules(e.currentTarget)}
            aria-label={`${game.name} rules and format`}
          >
            <BookOpen data-icon="inline-start" />
            Rules
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" aria-label={`${game.name} format and players`}>
          <Badge variant="secondary">{game.players}</Badge>
          <Badge variant="outline">{game.family}</Badge>
          <Badge variant="outline">{game.formatLabel}</Badge>
        </div>
      </CardHeader>

      <CardContent>
        <CardDescription className="text-sm leading-relaxed text-foreground/85">{game.subtitle}</CardDescription>
      </CardContent>

      <CardFooter className="gap-2.5 flex-wrap sm:flex-nowrap">
        <Button className="flex-1 min-h-11 font-semibold cursor-pointer" onClick={onPlaySolo}>
          <Play data-icon="inline-start" />
          Play Solo
        </Button>
        <Button variant="outline" className="flex-1 min-h-11 cursor-pointer" onClick={onOpenOnline}>
          <Users data-icon="inline-start" />
          {tableCount > 0 ? `Multiplayer (${tableCount})` : 'Multiplayer'}
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ── Community Requests & Voting Section (GitHub-Backed) ── */

function CommunityRequestsSection() {
  const searchInputId = useId();
  const user = useAuthStore((s) => s.user);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const signInAsTestMember = useAuthStore((s) => s.signInAsTestMember);
  const isSignedInMember = Boolean(user && user.provider !== 'guest');

  const [items, setItems] = useState<ApiCommunityRequestItem[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requestRevision, setRequestRevision] = useState(0);
  const [viewerVoteCount, setViewerVoteCount] = useState(0);
  const [maxVotes, setMaxVotes] = useState(5);
  const [githubNewGameUrl, setGithubNewGameUrl] = useState(
    'https://github.com/CardForest/issues/issues/new?template=game-request.yml&labels=game-request%2Cstatus%3Aproposed&title=%5BGame+Request%5D+',
  );
  const [githubNewFeatureUrl, setGithubNewFeatureUrl] = useState(
    'https://github.com/CardForest/issues/issues/new?template=feature-request.yml&labels=feature-request%2Cstatus%3Aproposed&title=%5BFeature+Request%5D+',
  );
  const [activeTab, setActiveTab] = useState<'accepted' | 'in-progress'>('accepted');
  const [query, setQuery] = useState('');
  const [busyIssueNumber, setBusyIssueNumber] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingRequests(true);
    setLoadError(null);
    setItems([]);
    setViewerVoteCount(0);
    void listCommunityRequests()
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setViewerVoteCount(res.viewerVoteCount);
        setMaxVotes(res.maxVotes);
        setGithubNewGameUrl(res.githubNewGameIssueUrl);
        setGithubNewFeatureUrl(res.githubNewFeatureIssueUrl);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Could not load community requests.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRequests(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.provider, requestRevision]);

  const trimmedQuery = query.trim();

  const openItems = useMemo(() => items.filter((item) => (item.stage ?? 'accepted') === 'accepted'), [items]);
  const inProgressItems = useMemo(() => items.filter((item) => item.stage === 'in-progress'), [items]);
  const itemsInTab = activeTab === 'accepted' ? openItems : inProgressItems;

  const rankByIssueNumber = useMemo(() => {
    const map = new Map<number, number>();
    openItems.forEach((item, index) => {
      map.set(item.issueNumber, index + 1);
    });
    return map;
  }, [openItems]);

  const filteredItems = useMemo(() => {
    if (!trimmedQuery) return itemsInTab;
    const q = trimmedQuery.toLowerCase();
    return itemsInTab.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        (item.subtitle?.toLowerCase().includes(q) ?? false),
    );
  }, [itemsInTab, trimmedQuery]);

  const handleToggleVote = async (item: ApiCommunityRequestItem) => {
    if (!isSignedInMember) {
      toast('Sign in required to vote', {
        description: 'Sign in with your account to cast up to 5 votes on upcoming games and features.',
        action: {
          label: import.meta.env.DEV ? 'Dev Sign In' : 'Sign in',
          onClick: () => {
            void (import.meta.env.DEV ? signInAsTestMember() : signInWithGoogle()).catch((error: unknown) => {
              toast.error(error instanceof Error ? error.message : 'Could not sign in. Please try again.');
            });
          },
        },
      });
      return;
    }

    if (busyIssueNumber !== null) return;
    setBusyIssueNumber(item.issueNumber);
    try {
      const response = item.viewerHasVoted
        ? await unvoteCommunityRequest(item.issueNumber)
        : await voteCommunityRequest(item.issueNumber);

      if (useAuthStore.getState().user?.id !== user?.id) return;
      setItems((prev) =>
        prev
          .map((existing) => (existing.issueNumber === item.issueNumber ? response.item : existing))
          .sort((a, b) => b.voteCount - a.voteCount || a.issueNumber - b.issueNumber),
      );
      setViewerVoteCount(response.viewerVoteCount);
      setMaxVotes(response.maxVotes);

      if (response.item.viewerHasVoted) {
        toast.success(`Voted for "${item.title}" (${response.viewerVoteCount}/${response.maxVotes} votes used)`);
      } else {
        toast(`Removed vote from "${item.title}" (${response.viewerVoteCount}/${response.maxVotes} votes used)`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message.replace(/^Request failed \(\d+\):\s*/, '') : 'Vote failed';
      toast.error(message);
    } finally {
      setBusyIssueNumber(null);
    }
  };

  const remainingVotes = Math.max(0, maxVotes - viewerVoteCount);
  const proposeGameUrl = `${githubNewGameUrl}${trimmedQuery ? encodeURIComponent(trimmedQuery) : ''}`;
  const proposeFeatureUrl = `${githubNewFeatureUrl}${trimmedQuery ? encodeURIComponent(trimmedQuery) : ''}`;

  return (
    <section aria-labelledby="catalog-heading" className="flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Layers className="size-4 text-brand" />
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">Community Priority Queue</span>
          </div>
          <h2 id="catalog-heading" className="text-xl sm:text-2xl font-bold tracking-tight">
            Vote on Upcoming Games &amp; Features
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Games and features share one unified priority queue. Each request starts with its requester's vote.
            Signed-in members get <strong className="font-medium text-foreground">up to {maxVotes} active votes</strong>{' '}
            on open requests — and whenever a request moves into development, your vote is automatically returned to
            spend on what comes next.
          </p>
        </div>

        {/* Active voter quota pill (shown only when signed in) */}
        {isSignedInMember && !loadingRequests && !loadError && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Badge variant={remainingVotes > 0 ? 'default' : 'secondary'} className="h-7 px-3 text-xs">
              {viewerVoteCount} / {maxVotes} votes used
            </Badge>
          </div>
        )}
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertTitle>Community requests unavailable</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
          <Button variant="outline" onClick={() => setRequestRevision((revision) => revision + 1)}>
            Retry
          </Button>
        </Alert>
      )}
      {loadingRequests && <p role="status">Loading community requests...</p>}

      {/* Tabs + Search + GitHub Submit */}
      {!loadingRequests && !loadError && (
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'accepted' | 'in-progress')}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="accepted">Open for Voting ({openItems.length})</TabsTrigger>
              <TabsTrigger value="in-progress">In Progress ({inProgressItems.length})</TabsTrigger>
            </TabsList>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-56">
                <label htmlFor={searchInputId} className="sr-only">
                  Search games and feature requests
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id={searchInputId}
                    type="search"
                    placeholder="Filter requests…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9 h-9 bg-card"
                  />
                </div>
              </div>

              <a
                href={proposeGameUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-xs font-medium hover:bg-muted transition-colors"
              >
                <Sparkles className="size-3.5 text-primary" />
                <span>Propose Game</span>
                <ExternalLink className="size-3 text-muted-foreground" />
              </a>
              <a
                href={proposeFeatureUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-xs font-medium hover:bg-muted transition-colors"
              >
                <span>Propose Feature</span>
                <ExternalLink className="size-3 text-muted-foreground" />
              </a>
            </div>
          </div>

          <TabsContent value={activeTab} className="mt-0">
            {filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredItems.map((item) => {
                  const isBusy = busyIssueNumber === item.issueNumber;
                  const limitReached = isSignedInMember && !item.viewerHasVoted && remainingVotes === 0;
                  const isInProgress = item.stage === 'in-progress';
                  const rank = rankByIssueNumber.get(item.issueNumber) ?? 1;
                  return (
                    <div
                      key={item.issueNumber}
                      data-testid={`community-request-${item.issueNumber}`}
                      className={`flex items-start justify-between gap-3 rounded-xl border p-3.5 text-sm transition-colors ${
                        isInProgress
                          ? 'border-l-[3px] border-l-brand bg-brand/[0.03]'
                          : 'bg-card hover:border-primary/40'
                      }`}
                    >
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {!isInProgress && (
                            <Badge variant="outline" className="tabular-nums font-semibold">
                              #{rank}
                            </Badge>
                          )}
                          <span className="font-semibold text-foreground">{item.title}</span>
                          <span
                            className={`inline-flex h-5 items-center rounded-full border px-2 text-[11px] font-medium ${
                              item.kind === 'game-request'
                                ? 'border-brand/25 bg-brand/10 text-brand'
                                : 'border-primary/30 bg-primary/10 text-primary'
                            }`}
                          >
                            {item.kind === 'game-request' ? 'Game' : 'Feature'}
                          </span>
                          <a
                            href={item.githubUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                            title={`View GitHub issue #${item.issueNumber}`}
                          >
                            <span>GitHub</span>
                            <ExternalLink className="size-3" />
                          </a>
                        </div>
                        {item.subtitle && <p className="text-xs font-medium text-muted-foreground">{item.subtitle}</p>}
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.summary}</p>
                      </div>

                      {isInProgress ? (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                          <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
                          Building
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant={item.viewerHasVoted ? 'default' : 'outline'}
                          className="min-h-11 shrink-0 gap-1.5 tabular-nums"
                          disabled={isBusy || limitReached}
                          onClick={() => void handleToggleVote(item)}
                          aria-pressed={item.viewerHasVoted}
                          aria-label={
                            item.viewerHasVoted
                              ? `Remove vote for ${item.title} (${item.voteCount} vote${item.voteCount === 1 ? '' : 's'})`
                              : `Vote for ${item.title} (${item.voteCount} vote${item.voteCount === 1 ? '' : 's'})`
                          }
                        >
                          <ArrowUp data-icon="inline-start" />
                          <span>{item.voteCount}</span>
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border bg-card p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex flex-col gap-1 text-center sm:text-left">
                  <p className="text-sm font-semibold">
                    No {activeTab === 'accepted' ? 'open request' : 'in-progress item'} matches &ldquo;{trimmedQuery}
                    &rdquo;
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Propose a new game or feature on GitHub. Once accepted, it will appear in the priority queue for
                    community voting.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <a
                    href={proposeGameUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 text-sm font-medium text-primary-foreground hover:bg-primary/85 transition-colors"
                  >
                    <Sparkles className="size-4" />
                    <span>Propose Game</span>
                    <ExternalLink className="size-3.5" />
                  </a>
                  <a
                    href={proposeFeatureUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border bg-background px-3.5 text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <span>Propose Feature</span>
                    <ExternalLink className="size-3.5" />
                  </a>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </section>
  );
}

/* ── Landing Page ─────────────────────────────────────── */

export function LandingPage() {
  const [tables, setTables] = useState<ApiLobbyTable[]>([]);
  const [membership, setMembership] = useState<ApiLobbyTable | undefined>();
  const [multiplayerGame, setMultiplayerGame] = useState<PlayableGame | null>(null);
  const [rulesGameInfo, setRulesGameInfo] = useState<GameInfo | null>(null);
  const [rulesReturnFocus, setRulesReturnFocus] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const authRevision = useAuthStore((s) => s.revision);
  const active = useRef(false);
  const refreshId = useRef(0);
  const refreshPending = useRef(false);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const openRulesForGame = useCallback(async (gameId: string, trigger: HTMLElement | null) => {
    setRulesReturnFocus(trigger);
    try {
      if (hasGameRules(gameId)) {
        const descriptor = games.list().find((game) => game.gameId === gameId);
        if (!descriptor) throw new Error(`Unknown game: ${gameId}`);
        setRulesGameInfo({
          gameId,
          displayName: descriptor.displayName,
          playerCount: descriptor.maxPlayers,
        });
        return;
      }
      const def = await games.load(gameId);
      setRulesGameInfo({
        gameId: def.gameId,
        displayName: def.displayName,
        playerCount: def.maxPlayers,
        infoCards: def.infoCards,
      });
    } catch (error) {
      console.error(`[Game rules ${gameId}] Could not load game rules:`, error);
      toast.error('Could not load game rules.');
    }
  }, []);

  const refresh = useCallback(
    async (force = false) => {
      if (refreshPending.current && !force) return;
      refreshPending.current = true;
      const id = ++refreshId.current;
      const isCurrent = () =>
        active.current && id === refreshId.current && useAuthStore.getState().revision === authRevision;
      try {
        const [updated, membership] = await Promise.all([
          listLobbyTables(session?.token ?? null),
          session ? getLobbyMembership(session.token) : Promise.resolve(null),
        ]);
        if (!isCurrent()) return;
        setTables(updated);
        setMembership(membership ?? undefined);
        if ((membership?.status === 'starting' || membership?.status === 'started') && membership.gameSessionId) {
          navigate(`/play?session=${encodeURIComponent(membership.gameSessionId)}`);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.warn('Could not load multiplayer tables:', error);
      } finally {
        if (id === refreshId.current) refreshPending.current = false;
      }
    },
    [navigate, session, authRevision],
  );

  useEffect(() => {
    setTables([]);
    setMembership(undefined);
    void refresh(true);
    const interval = setInterval(() => void refresh(), 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const myTable = membership?.seats.some((seat) => seat.userId === user?.id) ? membership : undefined;
  const myTableGame = myTable ? PLAYABLE_GAMES.find((g) => g.id === myTable.gameType) : undefined;

  const tableCountByGame = new Map<string, number>();
  for (const t of tables) {
    tableCountByGame.set(t.gameType, (tableCountByGame.get(t.gameType) ?? 0) + 1);
  }

  const totalOpenTables = tables.filter((t) => t.status === 'waiting' || t.status === 'full').length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-7 sm:py-9 flex flex-col gap-9">
      {/*Active Waiting Banner (when user is seated at an unstarted lobby table) */}
      {myTable && myTableGame && (
        <WaitingBanner
          table={myTable}
          game={myTableGame}
          onView={() => navigate(`/tables/${encodeURIComponent(myTable.tableId)}`)}
          onLeave={async () => {
            try {
              const isHost = myTable.hostUserId === user?.id;
              if (isHost) {
                await cancelLobbyTable(session?.token ?? null, myTable.tableId);
              } else {
                await leaveLobbyTable(session?.token ?? null, myTable.tableId);
              }
              if (active.current && useAuthStore.getState().revision === authRevision) void refresh(true);
            } catch (error) {
              if (active.current && useAuthStore.getState().revision === authRevision)
                toast.error(error instanceof Error ? error.message : 'Could not leave the table.');
            }
          }}
        />
      )}

      {/* Editorial Header + Playable Games Grid */}
      <section aria-labelledby="hero-heading" className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b pb-4">
          <div className="flex flex-col gap-1">
            <h1 id="hero-heading" className="text-2xl sm:text-3xl font-bold tracking-tight">
              Choose a Table
            </h1>
            <p className="text-sm text-muted-foreground">
              Play a solo match right away or open a live room with friends — no account required.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {totalOpenTables > 0 ? (
              <Badge variant="default" className="text-xs">
                <Users data-icon="inline-start" />
                {totalOpenTables} open {totalOpenTables === 1 ? 'table' : 'tables'} in lobby
              </Badge>
            ) : (
              <div className="hidden sm:flex items-center gap-2 text-muted-foreground/45" aria-hidden="true">
                <SuitGlyph suit="spades" className="size-3.5" />
                <SuitGlyph suit="hearts" className="size-3.5" />
                <SuitGlyph suit="clubs" className="size-3.5" />
                <SuitGlyph suit="diamonds" className="size-3.5" />
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PLAYABLE_GAMES.map((game) => (
            <PlayableGameCard
              key={game.id}
              game={game}
              tableCount={tableCountByGame.get(game.id) ?? 0}
              onPlaySolo={() => navigate(`/play?game=${game.id}`)}
              onOpenOnline={() => setMultiplayerGame(game)}
              onOpenRules={(trigger) => void openRulesForGame(game.id, trigger)}
            />
          ))}
        </div>
      </section>

      <Separator />

      {/* Community Roadmap & Voting (GitHub-Backed) */}
      <CommunityRequestsSection />

      {/* Focused Multiplayer Tables Dialog */}
      {multiplayerGame && (
        <MultiplayerDialog
          game={multiplayerGame}
          tables={tables}
          open={true}
          onOpenChange={(open) => {
            if (!open) setMultiplayerGame(null);
          }}
          onRefresh={() => void refresh(true)}
          myTable={myTable}
        />
      )}

      {/* Canonical Game Information / Rules Dialog (shared with in-game help) */}
      {rulesGameInfo && (
        <GameInfoDialog
          gameInfo={rulesGameInfo}
          open={true}
          onOpenChange={(open) => {
            if (!open) setRulesGameInfo(null);
          }}
          returnFocus={rulesReturnFocus}
        />
      )}
    </div>
  );
}
